/**
 * @fileoverview File upload validation utilities.
 * Validates filenames for special characters, length limits, and file size restrictions.
 */

/**
 * Fallback max file upload size in MB, used when no server env / per-bot value is available.
 * The authoritative ceiling comes from the server (FILE_UPLOAD_SIZE_LIMIT_MB).
 */
export const DEFAULT_FILE_UPLOAD_SIZE_MB = 35;

/**
 * Validates file upload against naming and size constraints.
 * Checks for non-ASCII characters, filename length (max 256), and file size.
 *
 * The size limit is configurable: callers pass the limit (in MB) resolved from the
 * `core.fileUploadSizeLimit` bot config, falling back to the env default (35MB).
 *
 * @param {File} file - File object to validate
 * @param {number} [maxSizeMB=35] - Maximum allowed file size in MB
 * @returns {{validated: boolean, errorMessage?: string}} Validation result
 *
 * @example
 * const result = validateFileUpload(file, 35);
 * if (!result.validated) {
 *   alert(result.errorMessage);
 * }
 */
export const validateFileUpload = (file, maxSizeMB = DEFAULT_FILE_UPLOAD_SIZE_MB) => {
  const { name, size, type } = file;

  const nonAsciiCharacters = name.match(/[^\x20-\x7F]/g);
  if (nonAsciiCharacters) {
    return {
      validated: false,
      errorMessage: `Please remove the following characters from the file name (${nonAsciiCharacters.join(
        ", "
      )}).`,
    };
  }

  const filenameTooLong = name.length > 256;
  if (filenameTooLong)
    return {
      validated: false,
      errorMessage: `File name should not exceed 256 characters `,
    };

  const filesizeTooBig = size > maxSizeMB * 1024 * 1024;
  if (filesizeTooBig)
    return { validated: false, errorMessage: `File size should not exceed ${maxSizeMB}MB` };

  return { validated: true };
};
